
/// <amd-module name="Wit/WorkItemDataManager/CacheManagers/WorkItemMetadataCacheInformationManager" />
import { format } from "VSS/Core/Util/String";
import { getCookie, setCookie } from "VSS/Platform/Util/Cookie";
import { WITCommonConstants, WorkItemTrackingMetadataCacheConstants } from "Wit/Common/Generated/Constants";
import { IVssPageContext } from "VSS/Platform/Context";
import { ILocalSettingsService, LocalSettingsScope } from "TFS/Services/LocalSettingsService";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IVssPageService } from "VSS/Platform/Page";

const CookieMaxAgeSeconds = 60 * 60 * 24 * 5; // Five days, after which the cookies will be cleaned up by the browser

const LocalStorageKey = "WIT-METADATA";

/** We want to set cookies only for certain meta data types */
const CookieEnabledMetadataTypes = {
    [WITCommonConstants.TeamProjects.toLowerCase()]: true,
    [WITCommonConstants.LinkTypes.toLowerCase()]: true,
    [WITCommonConstants.WorkItemTypes.toLowerCase()]: true
};

/**
 * Set meta data cookies for WIT experiences
 */
export class WorkItemMetadataCacheInformationManager {
    private _pageContext: IVssPageContext;

    public constructor(pageContext: IVssPageContext) {
        this._pageContext = pageContext;
    }

    /**
     * Stores WIT cache information as a cookie for the specified project and meta data type, as well as in local storage
     * for non-WIT experiences
     * @param scopeId Id of scope (typically collection or project id)
     * @param type Meta data type
     * @param keys Keys, if the meta data part can be subdivided (e.g., work item types)
     * @param stamp Cache stamp
     */
    public persist(scopeId: string, type: string, keys: string[], stamp: string | null): void {
        if (!this._shouldSetCookieForType(type) || !stamp) {
            return;
        }

        const cookieName = format(WorkItemTrackingMetadataCacheConstants.CookieFormat, scopeId);

        const localSettingsService = this._pageContext.getService<ILocalSettingsService>("ILocalSettingsService");

        // Cookies are set for WIT path (<collection>/<project/_workitems), one per project. Format:
        // <type>:<scopeId>:<stamp>:<name1>,<name2>|<type>:<scopeId>:<stamp>:...

        // Read existing cookie
        let cookieValue: string | undefined = getCookie(cookieName);
        if (!cookieValue) {
            cookieValue = localSettingsService.read<string>(LocalStorageKey, undefined, LocalSettingsScope.Project);
        }

        if (!cookieValue) {
            return;
        }

        const data: IWorkItemMetadataCookie = deserialize(cookieValue);

        // Update data
        data[type] = {
            scopeId,
            stamp,
            keys
        };

        cookieValue = serialize(data);

        const cookieLocation = this._getCookieLocation();

        if (cookieLocation) {
            setCookie(cookieName, cookieValue, cookieLocation, undefined, CookieMaxAgeSeconds);

            // Store in local storage as well, for non-wit experiences
            localSettingsService.write(LocalStorageKey, cookieValue, LocalSettingsScope.Project);
        }
    }

    /**
     * Gets the WIT cache information for non-WIT experiences
     */
    public readInformationForNonWITLocation(): string | undefined {
        const localSettingsService = this._pageContext.getService<ILocalSettingsService>("ILocalSettingsService");
        return localSettingsService.read(LocalStorageKey, undefined, LocalSettingsScope.Project);
    }

    private _shouldSetCookieForType(type: string): boolean {
        return CookieEnabledMetadataTypes[type.toLowerCase()];
    }

    private _getCookieLocation(): string | undefined {
        const projectName = getTFSPageData(this._pageContext)?.project?.name;
        const hostName = this._pageContext.getService<IVssPageService>("IVssPageService").getData()?.hostName;
        if (!projectName || !hostName) {
            return undefined;
        }
        // We don't want to send the cookie with every single request, so scope it to relevant WIT locations
        return `/${hostName}/${projectName}/_workitems/`;
    }
}

interface IWorkItemMetadataCookie {
    [typeName: string]: {
        /** Cache scope id (e.g., collection or project id) */
        scopeId: string;

        /** Cache stamp */
        stamp: string;

        /** Keys for meta data type (e.g., work item type names), "_" if there are no sub-types */
        keys: "_" | string[];
    };
}

const TypeSeparator = "|";
const TypeDataSeparator = ":";
const KeySeparator = ",";

function serialize(data: IWorkItemMetadataCookie): string {
    const parts: string[] = [];

    for (const typeName of Object.keys(data)) {
        const d = data[typeName];

        parts.push([typeName, d.scopeId, d.stamp, (Array.isArray(d.keys) && d.keys.join(KeySeparator)) || d.keys].join(TypeDataSeparator));
    }

    return encodeURIComponent(parts.join(TypeSeparator));
}

function deserialize(input: string): IWorkItemMetadataCookie {
    const data: IWorkItemMetadataCookie = {};

    const parts = decodeURIComponent(input || "").split(TypeSeparator);
    parts.forEach(typeValue => {
        const typeParts = typeValue.split(TypeDataSeparator);

        if (typeParts.length > 2) {
            const typename = typeParts[0];
            const scopeId = typeParts[1];
            const stamp = typeParts[2];
            const keys = typeParts[3].split(KeySeparator);

            data[typename] = {
                scopeId,
                stamp,
                keys: (keys.length > 0 && keys) || "_"
            };
        }
    });

    return data;
}
